import { useContainer } from '@/plugins/inversify';
import { ISearchService, ISearchServiceId } from '@/services';
import {
    type PlaceAutocompleteModelDto,
    type TripDto,
    GetTripModeDto,
    type LegDto,
    PlaceType,
    TripMode,
    type GetTripResponseMetadata,
    type TripPatternDto
} from '@/types/webapi';
import { defineStore, storeToRefs } from 'pinia';
import { isArray, isEmpty, isEqual, merge, mergeWith, omit } from 'lodash-es';
import { type TripSearchRequest, type TripSearchFilters, WalkSpeedType, TripPattern } from '@/features/trips/models';
import { computed, reactive, ref, toRefs, toValue, watch } from 'vue';
import { SearchMode, useMainStore } from '@/features/common/stores';
import { type SearchHistoryItem } from '@/features/places/models';
import { usePlaceAutocompleteHistory } from '@/features/places/composables';
import { isDefined } from '@vueuse/core';
import { DateType, SearchError } from '@/features/common/models';
import { isAxiosError, type AxiosError } from 'axios';
import { useGtm } from 'vue-gtm';
import { updateTripPatternBookingNotifications } from '@/features/bookings/composables';
import { useBookingStore } from '@/features/bookings/stores';
import { markAndFilterExpensiveTripPatterns, isTransfer, isTransitMode, sortTripPatternsByArrival } from '../helpers';
import { isRealtime } from '@/features/common/helpers';
import { usePlacesStore } from '@/features/places/stores';
import { useShapesStore } from '@/features/map/stores';

const defaultSearchFilters: TripSearchFilters = {
    modes: [],
    walkSpeed: WalkSpeedType.Normal,
    transferSlack: 120,
    showTripsWithTransfers: true,
    lines: []
};

const defaultSearchRequest: TripSearchRequest = {
    dateTime: undefined,
    dateTimeType: DateType.Now,
    filters: { ...defaultSearchFilters },
    nextPageCursors: {},
    previousPageCursors: {}
};

const defaultSearchResult = {
    errors: [] as string[],
    trip: null as TripDto | null,
    meta: null as GetTripResponseMetadata | null
};

const useTripSearchStore = defineStore('tripSearchStore', () => {
    const container = useContainer();
    const tripSearchService = container.get<ISearchService>(ISearchServiceId);
    const { addItem } = usePlaceAutocompleteHistory<SearchHistoryItem>('historyItems');
    const { addItem: addPastPlace } = usePlaceAutocompleteHistory<SearchHistoryItem>('pastPlaces');
    const mainStore = useMainStore();
    const { config, devModeConfiguration } = storeToRefs(mainStore);
    const placesStore = usePlacesStore();
    const { fromPlace, toPlace } = storeToRefs(placesStore);
    const shapesStore = useShapesStore();
    const gtm = useGtm();
    const bookingStore = useBookingStore();
    const blockResettingExpandedItem = ref(false);
    const savedTripPatterns = ref<string[]>([]);
    const ready = ref(false);
    const state = reactive({
        isDirty: false,
        searchRequest: { ...defaultSearchRequest },
        selectedTripPatternId: null as string | null,
        selectedTripPatternIdIsExpanded: false,
        selectedMode: GetTripModeDto.Bus
    });
    const searchResult = reactive({ ...defaultSearchResult });
    const _footSearchResult = reactive({ ...defaultSearchResult });
    const footSearchResult = computed(() =>
        _footSearchResult.trip ? Object.values(_footSearchResult.trip.tripPatternGroups)[0]?.[0] : undefined
    );
    const _bikeSearchResult = reactive({ ...defaultSearchResult });
    const bikeSearchResult = computed(() =>
        _bikeSearchResult.trip ? Object.values(_bikeSearchResult.trip.tripPatternGroups)[0]?.[0] : undefined
    );

    const searchingState = reactive({
        transitIsSearching: false,
        footIsSearching: false,
        bikeIsSearching: false
    });
    const loadingStates = reactive({
        transitIsLoading: false,
        footIsLoading: false,
        bikeIsLoading: false
    });
    const isSearching = computed(
        () => searchingState.transitIsSearching || searchingState.footIsSearching || searchingState.bikeIsSearching
    );
    const isLoading = computed(() => loadingStates.transitIsLoading || loadingStates.footIsLoading || loadingStates.bikeIsLoading);
    const canSearch = computed(() => isValidSearchRequest());
    const filterIsDirty = computed(() => !isEqual(toValue(state.searchRequest.filters), defaultSearchFilters));
    const hasResult = computed(() => Boolean(searchResult.trip));
    const tripPatternGroups = computed((): Record<string, TripPattern[]> =>
        updateTripPatternLegsRealtime(
            markAndFilterExpensiveTripPatterns(
                sortTripPatternsByArrival(
                    addTransferLegs(
                        removeDuplicateTripPatterns(
                            updateTripPatternBookingNotifications(
                                setIsMyLocation(removeHiddenLegs(searchResult.trip?.tripPatternGroups || {}), {
                                    from: fromPlace.value,
                                    to: toPlace.value
                                }),
                                bookingStore.bookings,
                                bookingStore.stopOrders
                            )
                        )
                    ),
                    state.searchRequest.dateTimeType === DateType.ArriveBy
                ),
                !config.value.isDebugModeEnabled,
                config.value.searchConfig.maxPatternsPerHour
            ),
            mainStore.currentTime
        )
    );
    const tripPatterns = computed(() => Object.values(tripPatternGroups.value).flatMap(x => x));
    const selectedTripPattern = computed((): TripPattern | undefined => {
        if (!state.selectedTripPatternId) return undefined;

        return (
            tripPatterns.value.find(x => x.id === state.selectedTripPatternId) ||
            (state.selectedTripPatternId === footSearchResult.value?.id && footSearchResult.value) ||
            (state.selectedTripPatternId === bikeSearchResult.value?.id && bikeSearchResult.value) ||
            undefined
        );
    });
    const generalizedCostMean = computed(() => {
        return tripPatterns.value.reduce((acc, tripPattern) => acc + (tripPattern.generalizedCost || 0), 0) / tripPatterns.value.length;
    });

    const isInNonTransitMode = computed(() =>
        [GetTripModeDto.Foot, GetTripModeDto.Bicycle].some(m => state.searchRequest.filters.modes.includes(m))
    );

    function $reset() {
        resetResult();
        state.isDirty = false;
        state.searchRequest = { ...defaultSearchRequest };
        state.selectedTripPatternId = null;
        state.selectedTripPatternIdIsExpanded = false;
        state.selectedMode = GetTripModeDto.Bus;
    }

    function resetResult() {
        Object.assign(searchResult, { ...defaultSearchResult });
        Object.assign(_bikeSearchResult, { ...defaultSearchResult });
        Object.assign(_footSearchResult, { ...defaultSearchResult });
        shapesStore.$reset();
    }

    function resetFilters() {
        state.searchRequest.filters = { ...defaultSearchFilters };
    }

    function isValidSearchRequest(): boolean {
        return (
            isDefined(fromPlace.value?.id) &&
            fromPlace.value?.type !== PlaceType.Unknown &&
            isDefined(toPlace.value?.id) &&
            toPlace.value?.type !== PlaceType.Unknown
        );
    }

    function selectFirstTripPattern() {
        if (!tripPatterns.value.length) return;

        state.selectedTripPatternId = tripPatterns.value[0].id;
    }

    let searchAbortController: AbortController | undefined;

    async function _executeSearch(searchFunc: Function, onError?: Function) {
        try {
            await searchFunc();
        } catch (e) {
            const { name } = e as AxiosError;
            // in case of AbortController being triggered, do nothing
            if (name === 'CanceledError') return;

            mainStore.registerError(e);
            onError?.();
        }
    }

    // foot mode search
    async function searchFoot({ signal }: { signal: AbortSignal }) {
        if (!isValidSearchRequest()) return;

        await _executeSearch(
            async () => {
                loadingStates.footIsLoading = true;
                searchingState.footIsSearching = true;
                // force mode
                const searchRequest = merge({}, state.searchRequest, {
                    fromPlace: fromPlace.value,
                    toPlace: toPlace.value,
                    filters: { modes: [GetTripModeDto.Foot] }
                });
                const searchResponse = await tripSearchService.searchForTrips(searchRequest, { signal });
                _footSearchResult.trip = searchResponse?.data || null;

                const { shapes = {} } = searchResponse?.data || {};

                // register trip shapes
                shapesStore.addShapes(shapes || {});

                searchingState.footIsSearching = false;
                loadingStates.footIsLoading = false;
            },
            () => {
                searchingState.footIsSearching = false;
                loadingStates.footIsLoading = false;
            }
        );
    }

    // bike mode search
    async function searchBike({ signal }: { signal: AbortSignal }) {
        if (!isValidSearchRequest()) return;

        loadingStates.bikeIsLoading = true;

        await _executeSearch(
            async () => {
                searchingState.bikeIsSearching = true;
                // force mode
                const searchRequest = merge({}, state.searchRequest, {
                    fromPlace: fromPlace.value,
                    toPlace: toPlace.value,
                    filters: { modes: [GetTripModeDto.Bicycle] }
                });
                const searchResponse = await tripSearchService.searchForTrips(searchRequest, { signal });
                _bikeSearchResult.trip = searchResponse?.data || null;

                const { shapes = {} } = searchResponse?.data || {};

                // register trip shapes
                shapesStore.addShapes(shapes || {});

                searchingState.bikeIsSearching = false;
                loadingStates.bikeIsLoading = false;
            },
            () => {
                searchingState.bikeIsSearching = false;
                loadingStates.bikeIsLoading = false;
            }
        );
    }

    async function searchTransit({ signal }: { signal: AbortSignal }) {
        if (!isValidSearchRequest()) return;

        loadingStates.transitIsLoading = true;

        await _executeSearch(
            async () => {
                searchingState.transitIsSearching = true;

                // TODO: check for bookings against booking store
                const searchResponse = await tripSearchService.searchForTrips(
                    { ...state.searchRequest, fromPlace: fromPlace.value, toPlace: toPlace.value },
                    { signal }
                );
                if (!searchResponse) return;

                const { data = null, meta = null } = searchResponse;
                const { shapes = {} } = data || {};

                searchResult.trip = data;
                searchResult.meta = meta;

                // register trip shapes
                shapesStore.addShapes(shapes);

                searchingState.transitIsSearching = false;
                loadingStates.transitIsLoading = false;
            },
            () => {
                searchingState.transitIsSearching = false;
                loadingStates.transitIsLoading = false;
            }
        );
    }

    function _search({ signal }: { signal: AbortSignal }) {
        if (signal.aborted) {
            return Promise.reject(new DOMException('Cancelled', 'CanceledError'));
        }

        return new Promise<void>((resolve, reject) => {
            // if searchRequest is not valid, do nothing
            if (!isValidSearchRequest()) return reject();

            signal.addEventListener('abort', () => reject(new DOMException('Cancelled', 'CanceledError')));

            // remove distance and isMyLocation
            const from = omit(fromPlace.value, ['distance', 'isMyLocation']);
            const to = omit(toPlace.value, ['distance', 'isMyLocation']);

            // save individual places
            addPastPlace({ id: from.id, from });
            addPastPlace({ id: to.id, from: to });

            // register history
            addItem({ id: `${from.id}__${to.id}`, from, to });

            Promise.all([searchTransit({ signal }), searchFoot({ signal }), searchBike({ signal })])
                .then(() => {
                    // track event
                    gtm?.trackEvent({
                        event: 'app_event',
                        category: 'trip_search',
                        action: 'search',
                        hasTransitStation: from.type === PlaceType.TransitStation || to.type === PlaceType.TransitStation,
                        hasDateTime: !!state.searchRequest.dateTime,
                        hasFilters: !isEqual(defaultSearchFilters, state.searchRequest.filters)
                    });

                    resolve();
                })
                .catch(reject);
        });
    }

    // search all modes
    async function search() {
        await _executeSearch(async () => {
            // if searchRequest is not valid, do nothing
            if (!isValidSearchRequest()) return;

            // abort previous request
            searchAbortController?.abort();
            searchAbortController = new AbortController();

            resetResult();
            state.isDirty = true;

            await _search({ signal: searchAbortController!.signal });
        });
    }

    function cancelSearch() {
        searchAbortController?.abort();
    }

    async function searchMore() {
        if (searchingState.transitIsSearching || !isValidSearchRequest()) return;

        loadingStates.transitIsLoading = true;

        try {
            if (!isEmpty(searchResult.meta?.nextPageCursors) || !isEmpty(searchResult.meta?.previousPageCursors)) {
                searchingState.transitIsSearching = true;

                const searchResponse = await tripSearchService.searchForMoreTrips(
                    merge({}, state.searchRequest, {
                        fromPlace: fromPlace.value,
                        toPlace: toPlace.value,
                        nextPageCursors: searchResult.meta?.nextPageCursors,
                        previousPageCursors: searchResult.meta?.previousPageCursors
                    })
                );

                if (!searchResponse) return;

                const {
                    data: { tripPatternGroups, shapes },
                    meta: { nextPageCursors, previousPageCursors }
                } = searchResponse;

                // we need to append trip patterns to groups
                mergeWith(searchResult.trip?.tripPatternGroups || {}, tripPatternGroups, mergeArray);

                // register trip shapes
                shapesStore.addShapes(shapes || {});

                // and save next page cursors
                if (searchResult.trip) {
                    searchResult.meta.nextPageCursors = nextPageCursors || {};
                    searchResult.meta.previousPageCursors = previousPageCursors || {};
                }
            }
        } catch (e) {
            mainStore.registerError(e);
        } finally {
            searchingState.transitIsSearching = false;
            loadingStates.transitIsLoading = false;
        }
    }

    /**
     * Loads trip pattern by id
     * @param id - The id of the trip pattern to load
     */
    async function loadTripPatternById(id: string) {
        if (!id) return;

        try {
            // load trip pattern by id
            const searchResponse = await tripSearchService.getTripByTripPatternId(id);
            if (!searchResponse) return;

            blockResettingExpandedItem.value = true;

            // set trip
            searchResult.trip = searchResponse.data;

            // set trip shapes
            shapesStore.addShapes(searchResponse.data.shapes || {});

            // set selected trip pattern id
            state.selectedTripPatternId = id;

            fromPlace.value = {
                id: searchResponse.data.from!.id!,
                type: searchResponse.data.from!.type,
                title: searchResponse.data.from!.name!,
                coordinates: searchResponse.data.from!.coordinates
            };
            toPlace.value = {
                id: searchResponse.data.to!.id!,
                type: searchResponse.data.to!.type,
                title: searchResponse.data.to!.name!,
                coordinates: searchResponse.data.to!.coordinates
            };
            Object.assign(state.searchRequest, {
                dateTime: searchResponse.data.dateTime,
                dateTimeType: DateType.DepartAt
            });
            state.isDirty = true;
        } catch (e) {
            // if no trip, reset selected trip pattern stuff
            searchResult.trip = null;
            state.selectedTripPatternId = null;

            if (isAxiosError(e)) {
                // show error for invalid trip pattern ID
                if (e.response?.status === 404) {
                    mainStore.registerError(new SearchError('trips.resources.errorMessages.tripNotFound'));
                }
            }
        } finally {
            blockResettingExpandedItem.value = false;
        }
    }

    async function saveTripPatternById(id: string) {
        if (!id) return false;

        try {
            if (!searchResult.trip) return false;

            // get the group id and where the trip pattern is located
            const groupId = Object.keys(searchResult.trip.tripPatternGroups).find(x =>
                Object.values(searchResult.trip!.tripPatternGroups[x]).some(y => y.id === id)
            );
            if (!groupId) return false;

            // get the trip pattern from the group
            const tripPattern = searchResult.trip.tripPatternGroups[groupId].find(x => x.id === id);
            if (!tripPattern) return false;

            // get trip shapes from leg and serviceJourney for the leg, if any
            const tripShapeIds: string[] = [
                ...new Set(
                    tripPattern.legs
                        ?.flatMap(l => [l.shapeRef, l.serviceJourney?.shapeRef])
                        .filter((ref): ref is string => isDefined(ref)) || []
                )
            ];
            const tripShapes = Object.fromEntries(
                Object.entries(searchResult.trip.shapes || {}).filter(([key]) => tripShapeIds.includes(key))
            );

            await tripSearchService.saveTripPattern({
                dateTime: tripPattern.aimedStartTime,
                from: searchResult.trip.from!,
                to: searchResult.trip.to!,
                tripPatternGroups: { [groupId]: [tripPattern] },
                shapes: tripShapes
            });
            savedTripPatterns.value.push(id);
            return true;
        } catch (e) {
            if (isAxiosError(e)) {
                if (e.response?.status === 404) {
                    mainStore.registerError(new SearchError('trips.resources.errorMessages.tripNotFound'));
                }
            } else {
                mainStore.registerError(e);
            }

            return false;
        }
    }

    function highlightShapesForTripPattern(tripPattern?: TripPatternDto) {
        shapesStore.unhighlightShapes();

        if (!tripPattern) return;

        const shapeRefs = (tripPattern.legs?.map(l => l.shapeRef).filter(isDefined) || []) as string[];
        shapesStore.highlightShapes(shapeRefs);
    }

    // reset selected trip pattern when mode changes
    watch(
        () => state.selectedMode,
        (value, oldValue) => {
            if (value === oldValue) return;

            state.selectedTripPatternId = null;
        }
    );

    watch(
        () => state.selectedTripPatternId,
        (value, oldValue) => {
            if (value === oldValue || blockResettingExpandedItem.value) return;

            // reset expanded item
            state.selectedTripPatternIdIsExpanded = false;
        }
    );

    // highlight shapes for selected trip pattern
    watch(selectedTripPattern, (tripPattern, oldTripPattern) => {
        if (tripPattern?.id === oldTripPattern?.id || blockResettingExpandedItem.value) return;

        highlightShapesForTripPattern(tripPattern);
    });

    watch(hasResult, value => {
        if (!value || !tripPatterns.value.length) {
            state.selectedTripPatternId = null;
            state.selectedTripPatternIdIsExpanded = false;
            return;
        }

        // check if selectedTripPatternId exists in list
        // if it doesn't, reset selected trip pattern stuff
        if (!tripPatterns.value.some(x => x.id === state.selectedTripPatternId)) {
            // TODO: show error for invalid trip pattern ID?
            state.selectedTripPatternId = null;
            state.selectedTripPatternIdIsExpanded = false;
        }
    });

    // trigger search when enturEndpointType changes
    watch(devModeConfiguration, async (value, oldValue) => {
        // if searchMode is not Trip or the values are equal, do nothing
        if (mainStore.searchMode !== SearchMode.Trip || isEqual(value, oldValue)) return;

        await search();
    });

    return {
        ...toRefs(state),
        ...toRefs(searchingState),
        ...toRefs(loadingStates),
        ready,
        isSearching,
        isLoading,
        canSearch,
        filterIsDirty,
        hasResult,
        tripPatterns,
        tripPatternGroups,
        selectedTripPattern,
        searchResult,
        footSearchResult,
        bikeSearchResult,
        savedTripPatterns,
        $reset,
        resetResult,
        search,
        searchMore,
        cancelSearch,
        resetFilters,
        selectFirstTripPattern,
        loadTripPatternById,
        saveTripPatternById,
        isInNonTransitMode,
        generalizedCostMean,
        highlightShapesForTripPattern
    };
});

function mergeArray(obj: Record<string, TripPattern[]>, src: Record<string, TripPattern[]>) {
    if (isArray(obj)) {
        return obj.concat(src);
    }
}

/**
 * Remove hidden legs from trip patterns
 * @param tripPatternGroups - The trip pattern groups to remove hidden legs from
 * @returns The trip pattern groups with hidden legs removed
 */
function removeHiddenLegs(tripPatternGroups: Record<string, TripPattern[]>): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            tripPatterns.map(x => ({
                ...x,
                legs: x.legs?.filter(y => !y.isHidden)
            }))
        ])
    );
}

/**
 * Set isMyLocation for origin/destination
 * @param tripPatternGroups - The trip pattern groups to set isMyLocation for
 * @param searchRequest - The search request
 * @returns The trip pattern groups with isMyLocation set for origin/destination
 */
function setIsMyLocation(
    tripPatternGroups: Record<string, TripPattern[]>,
    searchRequest: {
        from: (PlaceAutocompleteModelDto & { isMyLocation?: boolean }) | null;
        to: (PlaceAutocompleteModelDto & { isMyLocation?: boolean }) | null;
    }
): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            tripPatterns.map(tripPattern => {
                // check if isMyLocation is set for origin/destination
                const myLocationPlaceId = searchRequest.from?.isMyLocation
                    ? searchRequest.from.id
                    : searchRequest.to?.isMyLocation
                      ? searchRequest.to.id
                      : undefined;

                return {
                    ...tripPattern,
                    legs: tripPattern.legs?.map(leg => ({
                        ...leg,
                        from: { ...leg.from, isMyLocation: leg.from.id === myLocationPlaceId },
                        to: { ...leg.to, isMyLocation: leg.to.id === myLocationPlaceId }
                    }))
                };
            })
        ])
    );
}

/**
 * Remove duplicate trip patterns
 * @param tripPatternGroups - The trip pattern groups to remove duplicate trip patterns from
 * @returns The trip pattern groups with duplicate trip patterns removed
 */
function removeDuplicateTripPatterns(tripPatternGroups: Record<string, TripPattern[]>): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            tripPatterns.filter((tripPattern, index, self) => index === self.findIndex(t => t.id === tripPattern.id))
        ])
    );
}

// TODO: move this to backend
/**
 * Add transfer legs between transit legs i.e. when no quay change is needed
 * @param tripPatternGroups - The trip pattern groups to add transfer legs to
 * @returns The trip pattern groups with transfer legs added
 */
function addTransferLegs(tripPatternGroups: Record<string, TripPattern[]>): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            tripPatterns.map(tripPattern => ({
                ...tripPattern,
                legs: tripPattern.legs?.reduce<LegDto[]>((acc, leg, i, arr) => {
                    // if leg is a transit leg, check if the previous leg is also a transit leg
                    if (i > 0 && !isTransfer(leg) && isTransitMode(leg.tripMode)) {
                        const previousLeg = arr[i - 1];

                        // if previous leg is also a transit leg, add a transfer leg
                        if (isTransitMode(previousLeg.tripMode)) {
                            const transferLeg = {
                                id: `${previousLeg.id}_${leg.id}`,
                                from: previousLeg.to,
                                to: leg.from,
                                tripMode: TripMode.Foot,
                                distance: 0,
                                duration: 0,
                                aimedStartTime: previousLeg.aimedEndTime,
                                expectedStartTime: previousLeg.expectedEndTime,
                                aimedEndTime: leg.aimedStartTime,
                                expectedEndTime: leg.expectedStartTime,
                                intermediateEstimatedCalls: [],
                                notificationMessages: [],
                                isHidden: false,
                                isOperatedByKolumbus: leg.isOperatedByKolumbus
                            } satisfies LegDto;

                            return [...acc, transferLeg, leg];
                        }
                    }

                    return [...acc, leg];
                }, [])
            }))
        ])
    );
}

/**
 * Update trip pattern legs realtime values
 * @param tripPatternGroups - The trip pattern groups to update
 * @param currentTime - The current time
 * @returns The trip pattern groups with legs updated with realtime values
 */
function updateTripPatternLegsRealtime(tripPatternGroups: Record<string, TripPattern[]>, currentTime: Date): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            tripPatterns.map(tripPattern => ({
                ...tripPattern,
                legs: tripPattern.legs?.map(leg => ({
                    ...leg,
                    realtime: isRealtime(leg.realtime ?? false, currentTime, leg.aimedStartTime, leg.expectedStartTime)
                }))
            }))
        ])
    );
}

export default useTripSearchStore;
